import React, { useState, useEffect } from "react";
import { addWeeks, startOfWeek, format, parseISO } from 'date-fns';
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import { useFinancial } from "../../../context/FinancialContext";
import { useEmpresaSelector } from "../../../context/EmpresaSelectorContext";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../utils/api";
import "bulma/css/bulma.min.css";
import EmpresaBadge from "../../../components/EmpresaBadge";
import "../../../styles/resumen.css";
import "../../../styles/styles.css";

Chart.register(...registerables);

export default function Resumen() {
  const { activos, pasivos } = useFinancial();
  const { empresaSeleccionada, esConexAdmin } = useEmpresaSelector();
  const { usuario } = useAuth();
  const [loading, setLoading] = useState(true);

  // Filtros
  const currentYear = new Date().getFullYear();
  const anios = Array.from({ length: 10 }, (_, i) => (2020 + i).toString());
  const meses = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
  ];
  const dias = Array.from({ length: 31 }, (_, i) => (i + 1).toString().padStart(2, "0"));
  const [anioSeleccionado, setAnioSeleccionado] = useState("");
  const [mesSeleccionado, setMesSeleccionado] = useState("");
  const [diaSeleccionado, setDiaSeleccionado] = useState("");

  // Estados para datos de gráficos (empresa seleccionada)
  const [cuentasPorCobrarGraficos, setCuentasPorCobrarGraficos] = useState([]);
  const [cuentasPorPagarGraficos, setCuentasPorPagarGraficos] = useState([]);
  const [ingresosGraficos, setIngresosGraficos] = useState([]);
  const [egresosGraficos, setEgresosGraficos] = useState([]);
  const [patrimonioGraficos, setPatrimonioGraficos] = useState([]);

  // Estados para datos de resumen por empresa (todas las empresas para ConexAdmin)
  const [cuentasPorCobrarResumen, setCuentasPorCobrarResumen] = useState([]);
  const [cuentasPorPagarResumen, setCuentasPorPagarResumen] = useState([]);
  const [ingresosResumen, setIngresosResumen] = useState([]);
  const [egresosResumen, setEgresosResumen] = useState([]);
  const [patrimonioResumen, setPatrimonioResumen] = useState([]);

  // Cargar datos para gráficos (empresa seleccionada)
  useEffect(() => {
    const cargarDatosGraficos = async () => {
      // Datos para gráficos: siempre de la empresa seleccionada
      const empresaId = empresaSeleccionada?.id && empresaSeleccionada.id !== 'all' ? empresaSeleccionada.id : null;
      const qs = empresaId ? `?empresaId=${empresaId}` : '';
      // Cuentas por cobrar para gráficos
      try {
        const respCxC = await apiRequest(`/cuentas-por-cobrar${qs}`);
        const dataCxC = respCxC.ok ? await respCxC.json() : [];
        setCuentasPorCobrarGraficos(dataCxC);
      } catch (error) {
        console.error('[Resumen] ❌ Error CxC:', error);
        setCuentasPorCobrarGraficos([]);
      }

      // Cuentas por pagar para gráficos
      try {
        const respCxP = await apiRequest(`/cuentas-por-pagar${qs}`);
        setCuentasPorPagarGraficos(respCxP.ok ? await respCxP.json() : []);
      } catch { setCuentasPorPagarGraficos([]); }

      try {
        // Cargar ingresos para gráficos
        const responseIngresos = await apiRequest(`/ingresos${qs}`);
        if (responseIngresos.ok) {
          const dataIngresos = await responseIngresos.json();
          setIngresosGraficos(dataIngresos);
        }

        // Cargar egresos para gráficos
        const responseEgresos = await apiRequest(`/egresos${qs}`);
        if (responseEgresos.ok) {
          const dataEgresos = await responseEgresos.json();
          setEgresosGraficos(dataEgresos);
        }

        // Cargar patrimonio para gráficos (usando activos y pasivos del contexto filtrados por empresa)
        const patrimonioData = [
          ...activos.filter(a => !empresaId || a.empresaId === empresaId).map(a => ({
            id: a.id,
            fecha: a.fecha || new Date().toISOString().split('T')[0],
            valor: parseFloat(a.valorActivo) || 0,
            mes: new Date(a.fecha || new Date()).toLocaleDateString('es-ES', { month: 'long' }),
            anio: new Date(a.fecha || new Date()).getFullYear().toString(),
            dia: new Date(a.fecha || new Date()).getDate().toString().padStart(2, '0'),
            empresaId: a.empresaId
          })),
          ...pasivos.filter(p => !empresaId || p.empresaId === empresaId).map(p => ({
            id: p.id,
            fecha: p.fechaVencimiento || new Date().toISOString().split('T')[0],
            valor: parseFloat(p.montoActual) || 0,
            mes: new Date(p.fechaVencimiento || new Date()).toLocaleDateString('es-ES', { month: 'long' }),
            anio: new Date(p.fechaVencimiento || new Date()).getFullYear().toString(),
            dia: new Date(p.fechaVencimiento || new Date()).getDate().toString().padStart(2, '0'),
            empresaId: p.empresaId
          }))
        ];
        setPatrimonioGraficos(patrimonioData);

      } catch (error) {
        console.error('Error cargando datos para gráficos:', error);
      }
    };

    cargarDatosGraficos();
  }, [empresaSeleccionada?.id]); // Solo depender de empresaSeleccionada para recargar cuando cambie

  // Cargar datos para resumen por empresa (filtrar por empresa seleccionada)
  useEffect(() => {
    const cargarDatosResumen = async () => {
      // Para resumen: filtrar por empresa seleccionada, o todas si es "all"
      const empresaId = empresaSeleccionada?.id && empresaSeleccionada.id !== 'all' ? empresaSeleccionada.id : null;
      const qs = empresaId ? `?empresaId=${empresaId}` : '';

      // Cuentas por cobrar para resumen
      try {
        const respCxC = await apiRequest(`/cuentas-por-cobrar${qs}`);
        setCuentasPorCobrarResumen(respCxC.ok ? await respCxC.json() : []);
      } catch { setCuentasPorCobrarResumen([]); }

      // Cuentas por pagar para resumen
      try {
        const respCxP = await apiRequest(`/cuentas-por-pagar${qs}`);
        setCuentasPorPagarResumen(respCxP.ok ? await respCxP.json() : []);
      } catch { setCuentasPorPagarResumen([]); }

      try {
        setLoading(true);

        // Cargar ingresos para resumen
        const responseIngresos = await apiRequest(`/ingresos${qs}`);
        if (responseIngresos.ok) {
          const dataIngresos = await responseIngresos.json();
          setIngresosResumen(dataIngresos);
        }

        // Cargar egresos para resumen
        const responseEgresos = await apiRequest(`/egresos${qs}`);
        if (responseEgresos.ok) {
          const dataEgresos = await responseEgresos.json();
          setEgresosResumen(dataEgresos);
        }

        // Cargar patrimonio para resumen (usando activos y pasivos del contexto)
        const patrimonioData = [
          ...activos.map(a => ({
            id: a.id,
            fecha: a.fecha || new Date().toISOString().split('T')[0],
            valor: parseFloat(a.valorActivo) || 0,
            mes: new Date(a.fecha || new Date()).toLocaleDateString('es-ES', { month: 'long' }),
            anio: new Date(a.fecha || new Date()).getFullYear().toString(),
            dia: new Date(a.fecha || new Date()).getDate().toString().padStart(2, '0'),
            empresaId: a.empresaId
          })),
          ...pasivos.map(p => ({
            id: p.id,
            fecha: p.fechaVencimiento || new Date().toISOString().split('T')[0],
            valor: parseFloat(p.montoActual) || 0,
            mes: new Date(p.fechaVencimiento || new Date()).toLocaleDateString('es-ES', { month: 'long' }),
            anio: new Date(p.fechaVencimiento || new Date()).getFullYear().toString(),
            dia: new Date(p.fechaVencimiento || new Date()).getDate().toString().padStart(2, '0'),
            empresaId: p.empresaId
          }))
        ];
        setPatrimonioResumen(patrimonioData);

      } catch (error) {
        console.error('Error cargando datos para resumen:', error);
      } finally {
        setLoading(false);
      }
    };

    cargarDatosResumen();
  }, [empresaSeleccionada?.id, esConexAdmin]); // Solo depender de empresaSeleccionada para recargar cuando cambie

  // Función auxiliar para comparar fechas ignorando la hora y zona horaria
  const esMismoDia = (fecha1, fecha2) => {
    if (!fecha1 || !fecha2) return false;
    
    // Convertir ambas fechas a cadenas de fecha en formato YYYY-MM-DD para comparación
    const toLocalDateString = (dateStr) => {
      const date = new Date(dateStr);
      // Ajustar la fecha sumando la diferencia de la zona horaria
      const offset = date.getTimezoneOffset();
      const adjustedDate = new Date(date.getTime() + (offset * 60 * 1000));
      return adjustedDate.toISOString().split('T')[0];
    };
    
    return toLocalDateString(fecha1) === toLocalDateString(fecha2);
  };

  // Obtener componentes de fecha de un string de fecha
  const getDateComponents = (dateStr) => {
    if (!dateStr) return { anio: '', mes: '', dia: '' };
    
    try {
      const date = new Date(dateStr);
      // Ajustar por zona horaria
      const offset = date.getTimezoneOffset();
      const adjustedDate = new Date(date.getTime() + (offset * 60 * 1000));
      
      return {
        anio: adjustedDate.getFullYear().toString(),
        mes: (adjustedDate.getMonth() + 1).toString().padStart(2, '0'),
        mesNombre: adjustedDate.toLocaleDateString('es-ES', { month: 'long' }),
        dia: adjustedDate.getDate().toString().padStart(2, '0')
      };
    } catch (e) {
      console.error('Error al procesar fecha:', e);
      return { anio: '', mes: '', mesNombre: '', dia: '' };
    }
  };

  // Filtrar por año, mes y día
  const filtrar = (data) =>
    data.filter(e => {
      const fecha = e.fecha || new Date().toISOString().split('T')[0];
      const { anio, mes, mesNombre } = getDateComponents(fecha);
      
      // Si hay un filtro de fecha, verificar si coincide
      if (anioSeleccionado && anio !== anioSeleccionado) return false;
      if (mesSeleccionado && mesNombre.toLowerCase() !== mesSeleccionado.toLowerCase()) return false;
      if (diaSeleccionado) {
        const dia = getDateComponents(fecha).dia;
        if (dia !== diaSeleccionado) return false;
      }
      
      return true;
    });

  // Datos filtrados para gráficos (empresa seleccionada)
  const ingresosFiltradosGraficos = filtrar(ingresosGraficos);
  const egresosFiltradosGraficos = filtrar(egresosGraficos);
  const patrimonioFiltradoGraficos = filtrar(patrimonioGraficos);

  // Datos para resumen por empresa (sin filtrar por fecha para mostrar totales)
  const ingresosResumenSinFiltro = ingresosResumen;
  const egresosResumenSinFiltro = egresosResumen;
  const patrimonioResumenSinFiltro = patrimonioResumen;

  // Agrupar datos por empresa para resumen (filtrar por empresa seleccionada)
  const agruparPorEmpresa = (data, key = 'empresaId') => {
    // Si hay empresa seleccionada específica, todos los datos son de esa empresa
    if (empresaSeleccionada?.id && empresaSeleccionada.id !== 'all') {
      return { [empresaSeleccionada.id]: data };
    }

    // Si es "Todas las empresas" o no hay selección, agrupar por empresa
    const grouped = {};
    data.forEach(item => {
      const empresaId = item[key] || 'Sin Empresa';
      if (!grouped[empresaId]) grouped[empresaId] = [];
      grouped[empresaId].push(item);
    });
    return grouped;
  };

  const ingresosPorEmpresa = agruparPorEmpresa(ingresosResumen);
  const egresosPorEmpresa = agruparPorEmpresa(egresosResumen);
  const patrimonioPorEmpresa = agruparPorEmpresa(patrimonioResumen);
  const cxcPorEmpresa = agruparPorEmpresa(cuentasPorCobrarResumen);
  const cxpPorEmpresa = agruparPorEmpresa(cuentasPorPagarResumen);

  // Calcular totales por empresa
  const calcularTotalesPorEmpresa = () => {
    // Si hay empresa seleccionada específica, mostrar solo esa empresa
    if (empresaSeleccionada?.id && empresaSeleccionada.id !== 'all') {
      const empresaId = empresaSeleccionada.id;
      const ingresosEmp = ingresosPorEmpresa[empresaId] || [];
      const egresosEmp = egresosPorEmpresa[empresaId] || [];
      const patrimonioEmp = patrimonioPorEmpresa[empresaId] || [];
      const cxcEmp = cxcPorEmpresa[empresaId] || [];
      const cxpEmp = cxpPorEmpresa[empresaId] || [];

      const totalIngresos = ingresosEmp.reduce((acc, e) => acc + Number(e.monto || 0), 0);
      const totalEgresos = egresosEmp.reduce((acc, e) => acc + Number(e.monto || 0), 0);
      const totalPatrimonio = patrimonioEmp.reduce((acc, e) => acc + Number(e.valor || 0), 0);
      const totalCxC = cxcEmp.reduce((sum, c) => sum + Math.abs(parseFloat(c.saldoCancelado) || 0), 0);
      const totalCxP = cxpEmp.reduce((sum, c) => sum + (c.saldoAdeudado || 0), 0);

      // Redondear a 2 decimales hacia arriba
      const roundUp = (num) => Math.ceil(num * 100) / 100;

      return [{
        empresaId,
        empresaNombre: empresaSeleccionada.nombre,
        totalIngresos: roundUp(totalIngresos),
        totalEgresos: roundUp(totalEgresos),
        totalPatrimonio: roundUp(totalPatrimonio),
        totalCxC: roundUp(totalCxC),
        totalCxP: roundUp(totalCxP),
        countIngresos: ingresosEmp.length,
        countEgresos: egresosEmp.length,
        countCxC: cxcEmp.length,
        countCxP: cxpEmp.length
      }];
    }

    // Si es "Todas las empresas", mostrar todas agrupadas
    const empresas = new Set([
      ...Object.keys(ingresosPorEmpresa),
      ...Object.keys(egresosPorEmpresa),
      ...Object.keys(patrimonioPorEmpresa),
      ...Object.keys(cxcPorEmpresa),
      ...Object.keys(cxpPorEmpresa)
    ]);

    return Array.from(empresas).map(empresaId => {
      const ingresosEmp = ingresosPorEmpresa[empresaId] || [];
      const egresosEmp = egresosPorEmpresa[empresaId] || [];
      const patrimonioEmp = patrimonioPorEmpresa[empresaId] || [];
      const cxcEmp = cxcPorEmpresa[empresaId] || [];
      const cxpEmp = cxpPorEmpresa[empresaId] || [];

      const totalIngresos = ingresosEmp.reduce((acc, e) => acc + Number(e.monto || 0), 0);
      const totalEgresos = egresosEmp.reduce((acc, e) => acc + Number(e.monto || 0), 0);
      const totalPatrimonio = patrimonioEmp.reduce((acc, e) => acc + Number(e.valor || 0), 0);
      const totalCxC = cxcEmp.reduce((sum, c) => sum + Math.abs(parseFloat(c.saldoCancelado) || 0), 0);
      const totalCxP = cxpEmp.reduce((sum, c) => sum + (c.saldoAdeudado || 0), 0);

      // Redondear a 2 decimales hacia arriba
      const roundUp = (num) => Math.ceil(num * 100) / 100;

      return {
        empresaId,
        empresaNombre: empresaId === 'Sin Empresa' ? 'Sin Empresa' : (empresaId === empresaSeleccionada?.id ? empresaSeleccionada.nombre : `Empresa ${empresaId}`),
        totalIngresos: roundUp(totalIngresos),
        totalEgresos: roundUp(totalEgresos),
        totalPatrimonio: roundUp(totalPatrimonio),
        totalCxC: roundUp(totalCxC),
        totalCxP: roundUp(totalCxP),
        countIngresos: ingresosEmp.length,
        countEgresos: egresosEmp.length,
        countCxC: cxcEmp.length,
        countCxP: cxpEmp.length
      };
    });
  };

  // Calcular totales de ingresos pagados desde cierres-caja
  const calcularTotalesIngresosPagados = async () => {
    try {
      const empresaId = empresaSeleccionada?.id && empresaSeleccionada.id !== 'all' ? empresaSeleccionada.id : null;
      const qs = empresaId ? `?empresaId=${empresaId}` : '';

      const response = await apiRequest(`/cierres-caja${qs}`);
      if (response.ok) {
        const cierres = await response.json();

        // Filtrar solo ventas (no cierres diarios) y calcular totales
        const ventasManuales = cierres.filter(c => c.tipoPago && c.producto && c.cantidad);

        // Total de la columna "Pagado" = suma de todos los valores en la columna pagado
        // Para ventas normales: pagado es el monto pagado
        // Para porPagar: pagado es el adeudado (total - abono)
        const totalPagado = ventasManuales.reduce((total, venta) => total + parseFloat(venta.pagado || 0), 0);

        // Redondear a 2 decimales hacia arriba
        const roundUp = (num) => Math.ceil(num * 100) / 100;

        return {
          totalIngresosPagados: roundUp(totalPagado),
          countIngresosPagados: ventasManuales.length
        };
      }
    } catch (error) {
      console.error('Error calculando totales de ingresos pagados:', error);
    }
    return { totalIngresosPagados: 0, countIngresosPagados: 0 };
  };

  // Calcular el "Total Pagado" del resumen de CierreCaja
  const calcularTotalPagadoResumen = async () => {
    try {
      const empresaId = empresaSeleccionada?.id && empresaSeleccionada.id !== 'all' ? empresaSeleccionada.id : null;
      const qs = empresaId ? `?empresaId=${empresaId}` : '';

      const response = await apiRequest(`/cierres-caja${qs}`);
      if (response.ok) {
        const cierres = await response.json();

        // Filtrar solo ventas (no cierres diarios)
        const ventasManuales = cierres.filter(c => c.tipoPago && c.producto && c.cantidad);

        // Calcular exactamente como en CierreCaja.jsx: totalAbonos + totalPagadoNormal
        const totalAbonos = ventasManuales
          .filter(venta => venta.tipoPago === 'porPagar')
          .reduce((total, venta) => total + parseFloat(venta.abono || 0), 0);

        const totalPagadoNormal = ventasManuales
          .filter(venta => venta.tipoPago !== 'porPagar')
          .reduce((total, venta) => total + parseFloat(venta.pagado || 0), 0);

        const totalPagado = totalAbonos + totalPagadoNormal;

        // Para evitar valores negativos, asegurarse de que sea positivo
        const totalPagadoFinal = Math.max(0, totalPagado);

        // Redondear a 2 decimales hacia arriba
        const roundUp = (num) => Math.ceil(num * 100) / 100;

        return roundUp(totalPagadoFinal);
      }
    } catch (error) {
      console.error('Error calculando total pagado del resumen:', error);
    }
    return 0;
  };

  // Estado para totales de ingresos pagados
  const [totalesIngresosPagados, setTotalesIngresosPagados] = useState({ totalIngresosPagados: 0, countIngresosPagados: 0 });
  const [totalPagadoResumen, setTotalPagadoResumen] = useState(0);

  // Cargar totales de ingresos pagados
  useEffect(() => {
    const cargarTotalesIngresosPagados = async () => {
      const totales = await calcularTotalesIngresosPagados(empresaSeleccionada);
      setTotalesIngresosPagados(totales);

      // Cargar también el total pagado del resumen
      const totalPagado = await calcularTotalPagadoResumen();
      setTotalPagadoResumen(totalPagado);
    };
    cargarTotalesIngresosPagados();
  }, [empresaSeleccionada?.id]);

  const totalesPorEmpresa = calcularTotalesPorEmpresa();

  // Calcular totales para gráficos (empresa seleccionada)
  const totalCxCGraficos = cuentasPorCobrarGraficos.reduce((sum, c) => sum + Math.abs(parseFloat(c.saldoCancelado) || 0), 0);
  const totalCxPGraficos = cuentasPorPagarGraficos.reduce((sum, c) => sum + (c.saldoAdeudado || 0), 0);
  const totalIngresosGraficos = ingresosFiltradosGraficos.reduce((acc, e) => acc + Number(e.monto || 0), 0);
  const totalEgresosGraficos = egresosFiltradosGraficos.reduce((acc, e) => acc + Number(e.monto || 0), 0);
  const totalPatrimonioGraficos = patrimonioFiltradoGraficos.reduce((acc, e) => acc + Number(e.valor || 0), 0);

  // Calcular totales para resumen (todas las empresas para ConexAdmin)
  const totalCxCResumen = cuentasPorCobrarResumen.reduce((sum, c) => sum + Math.abs(parseFloat(c.saldoCancelado) || 0), 0);
  const totalCxPResumen = cuentasPorPagarResumen.reduce((sum, c) => sum + (c.saldoAdeudado || 0), 0);
  const totalIngresosResumen = ingresosResumenSinFiltro.reduce((acc, e) => acc + Number(e.monto || 0), 0);
  const totalEgresosResumen = egresosResumenSinFiltro.reduce((acc, e) => acc + Number(e.monto || 0), 0);
  const totalPatrimonioResumen = patrimonioResumenSinFiltro.reduce((acc, e) => acc + Number(e.valor || 0), 0);

  // Totales generales (para compatibilidad)
  const totalCxC = Math.abs(totalesPorEmpresa.reduce((sum, emp) => sum + emp.totalCxC, 0));
  const totalCxP = totalesPorEmpresa.reduce((sum, emp) => sum + emp.totalCxP, 0);
  const totalIngresos = totalesPorEmpresa.reduce((sum, emp) => sum + emp.totalIngresos, 0);
  const totalEgresos = totalesPorEmpresa.reduce((sum, emp) => sum + emp.totalEgresos, 0);
  const totalPatrimonio = totalesPorEmpresa.reduce((sum, emp) => sum + emp.totalPatrimonio, 0);

  // Agrupar CxC, CxP por semana y mes
  function getWeekKey(dateStr) {
    const d = parseISO(dateStr.split('T')[0]);
    const year = d.getFullYear();
    const week = Math.ceil((d - startOfWeek(new Date(year,0,1), { weekStartsOn: 1 })) / (7 * 24 * 60 * 60 * 1000)) + 1;
    return `${year}-W${week.toString().padStart(2,'0')}`;
  }
  function getMonthKey(dateStr) {
    const d = parseISO(dateStr.split('T')[0]);
    return `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2,'0')}`;
  }

  // Agrupación semanal (usando datos de gráficos - empresa seleccionada)
  const semanas = {};
  cuentasPorCobrarGraficos.forEach(c => {
    const key = getWeekKey(c.fechaFactura || c.fecha || c.fechaVencimiento || new Date());
    semanas[key] = semanas[key] || { cobrar: 0, pagar: 0 };
    semanas[key].cobrar += parseFloat(c.saldoCancelado) || 0;
  });
  cuentasPorPagarGraficos.forEach(c => {
    const key = getWeekKey(c.fechaFactura || c.fecha || c.fechaVencimiento || new Date());
    semanas[key] = semanas[key] || { cobrar: 0, pagar: 0 };
    semanas[key].pagar += parseFloat(c.saldoAdeudado) || 0;
  });
  const labelsSemanas = Object.keys(semanas).sort();
  const datosGraficoSemanal = {
    labels: labelsSemanas,
    datasets: [
      { label: 'Cuentas por Cobrar', data: labelsSemanas.map(k => semanas[k].cobrar), borderColor: '#4CAF50', backgroundColor: 'rgba(76, 175, 80, 0.15)', fill: true, tension: 0.3 },
      { label: 'Cuentas por Pagar', data: labelsSemanas.map(k => semanas[k].pagar), borderColor: '#F44336', backgroundColor: 'rgba(244, 67, 54, 0.15)', fill: true, tension: 0.3 },
    ],
  };

  // Agrupación mensual (usando datos de gráficos - empresa seleccionada)
  const mesesG = {};
  cuentasPorCobrarGraficos.forEach(c => {
    const key = getMonthKey(c.fechaFactura || c.fecha || c.fechaVencimiento || new Date());
    mesesG[key] = mesesG[key] || { cobrar: 0, pagar: 0 };
    mesesG[key].cobrar += parseFloat(c.saldoCancelado) || 0;
  });
  cuentasPorPagarGraficos.forEach(c => {
    const key = getMonthKey(c.fechaFactura || c.fecha || c.fechaVencimiento || new Date());
    mesesG[key] = mesesG[key] || { cobrar: 0, pagar: 0 };
    mesesG[key].pagar += parseFloat(c.saldoAdeudado) || 0;
  });
  const labelsMeses = Object.keys(mesesG).sort();
  const datosGraficoMensual = {
    labels: labelsMeses,
    datasets: [
      { label: 'Cuentas por Cobrar', data: labelsMeses.map(k => mesesG[k].cobrar), borderColor: '#4CAF50', backgroundColor: 'rgba(76, 175, 80, 0.15)', fill: true, tension: 0.3 },
      { label: 'Cuentas por Pagar', data: labelsMeses.map(k => mesesG[k].pagar), borderColor: '#F44336', backgroundColor: 'rgba(244, 67, 54, 0.15)', fill: true, tension: 0.3 },
    ],
  };
  
  return (
    <div className="resumen-container">
      <div className="container is-max-desktop is-centered">
        {/* Navegación de pestañas */}
        <div className="tabs is-centered is-boxed mb-5">
          <ul>
            <li>
              <a href="/dashboard/contabilidad/ingresos">
                <span className="icon is-small"><i className="fas fa-dollar-sign" aria-hidden="true"></i></span>
                <span>Ingresos</span>
              </a>
            </li>
            <li>
              <a href="/dashboard/contabilidad/egresos">
                <span className="icon is-small"><i className="fas fa-minus" aria-hidden="true"></i></span>
                <span>Egresos</span>
              </a>
            </li>
            <li>
              <a href="/dashboard/contabilidad/patrimonio">
                <span className="icon is-small"><i className="fas fa-chart-pie" aria-hidden="true"></i></span>
                <span>Patrimonio</span>
              </a>
            </li>
            <li>
              <a href="/dashboard/contabilidad/calculo-costos">
                <span className="icon is-small"><i className="fas fa-calculator" aria-hidden="true"></i></span>
                <span>Cálculo de Costos</span>
              </a>
            </li>
            <li className="is-active">
              <a>
                <span className="icon is-small"><i className="fas fa-chart-bar" aria-hidden="true"></i></span>
                <span>Resumen</span>
              </a>
            </li>
          </ul>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16 }}>
          <h1 className="resumen-title" style={{ marginBottom: 0 }}>Resumen Financiero</h1>
          <EmpresaBadge />
          <div style={{ fontWeight: 'bold', fontSize: 18, marginTop: 8, marginBottom: 8 }}>
                      </div>
        </div>
        
        {loading && (
          <div className="notification is-info has-text-centered">
            <p>Cargando datos financieros...</p>
          </div>
        )}

        {/* Filtros año, mes, día */}
        <div className="columns is-vcentered mb-4 is-centered" style={{ maxWidth: 900, margin: '0 auto' }}>
          <div className="column is-3">
            <label className="label">Año</label>
            <div className="select is-fullwidth">
              <select value={anioSeleccionado} onChange={(e) => setAnioSeleccionado(e.target.value)}>
                {anios.map(anio => <option key={anio} value={anio}>{anio}</option>)}
              </select>
            </div>
          </div>
          <div className="column is-4">
            <label className="label">Mes</label>
            <div className="select is-fullwidth">
              <select value={mesSeleccionado} onChange={(e) => setMesSeleccionado(e.target.value)}>
                <option value="">Todos los meses</option>
                {meses.map(mes => <option key={mes} value={mes}>{mes}</option>)}
              </select>
            </div>
          </div>
          <div className="column is-3">
            <label className="label">Día</label>
            <div className="select is-fullwidth">
              <select value={diaSeleccionado} onChange={(e) => setDiaSeleccionado(e.target.value)}>
                <option value="">Todos los días</option>
                {dias.map(dia => <option key={dia} value={dia}>{dia}</option>)}
              </select>
            </div>
          </div>
          <div className="column is-2" style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button className="button is-light is-fullwidth" onClick={() => { setAnioSeleccionado(currentYear.toString()); setMesSeleccionado(""); setDiaSeleccionado(""); }}>Reset</button>
          </div>
        </div>

        {/* Totales generales */}
        <div className="columns is-multiline mb-4 is-centered">
          <div className="column is-6">
            <div className="box is-gold has-text-black has-text-weight-semibold has-text-centered">
              Total Cuentas por Cobrar: ${Math.round(totalCxC * 100) / 100}
            </div>
          </div>
          <div className="column is-6">
            <div className="box is-gold has-text-black has-text-weight-semibold has-text-centered">
              Total Cuentas por Pagar: ${Math.round(totalCxP * 100) / 100}
            </div>
          </div>
        </div>

        {/* Gráficos NUEVO */}
        <div className="columns is-multiline mb-4 is-centered">
          <div className="column is-6">
            <h2 className="subtitle has-text-darkblue has-text-centered">Gráfico Semanal</h2>
            {labelsSemanas.length > 0 ? (
              <div style={{ height: 240 }}>
                <Line data={datosGraficoSemanal} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true } } }} />
              </div>
            ) : (
              <div className="notification is-light has-text-centered">
                <p>No hay datos para mostrar en el gráfico semanal</p>
              </div>
            )}
          </div>
          <div className="column is-6">
            <h2 className="subtitle has-text-darkblue has-text-centered">Gráfico Mensual</h2>
            {labelsMeses.length > 0 ? (
              <div style={{ height: 260 }}>
                <Line data={datosGraficoMensual} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } }, scales: { y: { beginAtZero: true } } }} />
              </div>
            ) : (
              <div className="notification is-light has-text-centered">
                <p>No hay datos para mostrar en el gráfico mensual</p>
              </div>
            )}
          </div>
        </div>

        {/* Tabla de resumen */}
        <div className="table-container">
          <table className="table is-bordered is-fullwidth is-striped">
            <thead>
              <tr className="has-background-grey-lighter has-text-black">
                <th style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Categoría</th>
                <th style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Total Ingresos Pagados</th>
                <th style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Total Egresos Pagados</th>
                <th style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Cuentas por Cobrar</th>
                <th style={{ fontSize: '1.1em', fontWeight: 'bold' }}>Cuentas por Pagar</th>
              </tr>
            </thead>
            <tbody className="has-text-black">
              {/* Fila única con totales */}
              <tr className="has-background-light">
                <td className="has-text-weight-bold has-text-centered" style={{ fontSize: '1.1em' }}>
                  <strong>Cantidad</strong><br/>
                  <strong><span style={{ fontSize: '0.9em' }}>Total</span></strong>
                </td>
                <td className="has-text-weight-bold has-text-centered" style={{ fontSize: '1.1em' }}>
                  {totalesIngresosPagados.countIngresosPagados}<br/>
                  <span className="has-text-weight-normal" style={{ fontSize: '0.9em' }}>${totalPagadoResumen.toFixed(2)}</span>
                </td>
                <td className="has-text-weight-bold has-text-centered" style={{ fontSize: '1.1em' }}>
                  {totalesPorEmpresa.reduce((sum, emp) => sum + emp.countEgresos, 0)}<br/>
                  <span className="has-text-weight-normal" style={{ fontSize: '0.9em' }}>${totalEgresos.toFixed(2)}</span>
                </td>
                <td className="has-text-weight-bold has-text-centered" style={{ fontSize: '1.1em' }}>
                  {totalesPorEmpresa.reduce((sum, emp) => sum + emp.countCxC, 0)}<br/>
                  <span className="has-text-weight-normal" style={{ fontSize: '0.9em' }}>${totalCxC.toFixed(2)}</span>
                </td>
                <td className="has-text-weight-bold has-text-centered" style={{ fontSize: '1.1em' }}>
                  {totalesPorEmpresa.reduce((sum, emp) => sum + emp.countCxP, 0)}<br/>
                  <span className="has-text-weight-normal" style={{ fontSize: '0.9em' }}>${totalCxP.toFixed(2)}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        
        {/* Información adicional - Solo para conexAdmin */}
        {esConexAdmin && (
          <div className="notification is-info is-light mt-4">
            <div className="columns is-multiline">
              <div className="column is-12">
                <strong>Totales Generales:</strong>
                <ul style={{ marginTop: '0.5rem' }}>
                  <li><strong>Total Ingresos Pagados:</strong> {totalesIngresosPagados.countIngresosPagados} registros (${totalPagadoResumen.toFixed(2)})</li>
                  <li><strong>Total Egresos Pagados:</strong> {totalesPorEmpresa.reduce((sum, emp) => sum + emp.countEgresos, 0)} registros (${totalEgresos.toFixed(2)})</li>
                  <li><strong>Cuentas por Cobrar:</strong> {totalesPorEmpresa.reduce((sum, emp) => sum + emp.countCxC, 0)} registros (${totalCxC.toFixed(2)})</li>
                  <li><strong>Cuentas por Pagar:</strong> {totalesPorEmpresa.reduce((sum, emp) => sum + emp.countCxP, 0)} registros (${totalCxP.toFixed(2)})</li>
                  <li><strong>Empresa actual:</strong> {empresaSeleccionada?.id === 'all' ? 'Todas las empresas' : empresaSeleccionada?.nombre || 'Sin empresa'}</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
